import { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import { useTrendDetectionService } from 'features/trend-detection/service'
import type { IssueTrackingMutationResponse } from 'features/trend-detection/types'

/**
 * Owns the track-with-email opt-in modal state for any page
 * that fires `service.trackIssue`. Reads the server's prompt decision from
 * the track response, fires the Mixpanel "shown" event, and exposes
 * stable handlers for the modal's two buttons. Wire one hook per page;
 * call `handleTrackResponse(response, issueUuid)` after every successful
 * track to surface the prompt when the server says so.
 */
interface PromptState {
  agentUuid: string
  agentName: string
  issueUuid: string
}

export interface UseTrackEmailOptInPromptResult {
  isOpen: boolean
  agentName: string
  loading: boolean
  handleTrackResponse: (
    response: IssueTrackingMutationResponse,
    triggerIssueUuid: string,
  ) => void
  onOptIn: () => Promise<void>
  onDecline: () => Promise<void>
}

export function useTrackEmailOptInPrompt(
  projectId: string,
): UseTrackEmailOptInPromptResult {
  const service = useTrendDetectionService()
  const [state, setState] = useState<PromptState | null>(null)
  const [loading, setLoading] = useState(false)
  // Synchronous re-entrancy guard. `loading` drives the UI, but a state read
  // inside `answer` can be stale across the click→re-render gap — so a dismiss
  // (Escape/backdrop) landing mid-flight could fire a second answer with the
  // opposite opt_in. The ref flips synchronously and closes that window.
  const inFlightRef = useRef(false)

  const handleTrackResponse = useCallback(
    (response: IssueTrackingMutationResponse, triggerIssueUuid: string) => {
      if (
        !response.should_prompt_email_opt_in ||
        !response.prompt_agent_uuid ||
        !response.prompt_agent_name
      ) {
        return
      }
      const next: PromptState = {
        agentUuid: response.prompt_agent_uuid,
        agentName: response.prompt_agent_name,
        issueUuid: triggerIssueUuid,
      }
      setState(next)
      trackResearchAgents(ResearchAgentsAction.track_email_prompt_shown, {
        agent_id: next.agentUuid,
        agent_name: next.agentName,
        issue_uuid: next.issueUuid,
      })
    },
    [],
  )

  const answer = useCallback(
    async (optIn: boolean) => {
      if (state === null || inFlightRef.current) return
      const current = state
      inFlightRef.current = true
      setLoading(true)
      try {
        await service.answerEmailPrompt(projectId, current.agentUuid, optIn)
        trackResearchAgents(ResearchAgentsAction.track_email_prompt_answered, {
          agent_id: current.agentUuid,
          agent_name: current.agentName,
          opt_in: optIn,
        })
        if (optIn) {
          toast.success(`Emails on for ${current.agentName}`)
        }
        setState(null)
      } catch {
        toast.error(
          "Couldn't save your preference. Try again from Agent settings.",
        )
        setState(null)
      } finally {
        setLoading(false)
        inFlightRef.current = false
      }
    },
    [service, projectId, state],
  )

  const onOptIn = useCallback(() => answer(true), [answer])
  const onDecline = useCallback(() => answer(false), [answer])

  return {
    isOpen: state !== null,
    agentName: state?.agentName ?? '',
    loading,
    handleTrackResponse,
    onOptIn,
    onDecline,
  }
}
